/**
 * `console.*` forwarding to the OTLP logs pipeline (Kapitalvins parity).
 *
 * When enabled, wraps the chosen `console` methods so each call ALSO emits an OTel
 * log record (carrying `session.id` + end-user identity, severity-mapped) before
 * delegating to the original console — the page's own console output is never
 * suppressed, only mirrored.
 *
 * Opt-in (`forwardConsole`), because console output can be noisy and may contain
 * PII the host didn't intend to ship; the SDK stays quiet by default. Invariant
 * (1.1/1.2/1.3): never break the host page. Each wrapper is `try/catch`-guarded
 * and re-entrancy-guarded (so emitting a record can't recurse back through a
 * patched console), and the originals are restored on `stop()`.
 */
import type { Logger } from '@opentelemetry/api-logs'
import { SeverityNumber } from '@opentelemetry/api-logs'

import { emitLogRecord, setupOtelLogs } from './otel-logs.js'
import type { ObservInitOptions } from './types.js'

/** Console methods we can forward, in increasing severity. */
export type ConsoleLevel = 'debug' | 'log' | 'info' | 'warn' | 'error'

/** Console level → OTel severity (number + canonical text). */
const LEVELS: Record<ConsoleLevel, { number: SeverityNumber; text: string }> = {
  debug: { number: SeverityNumber.DEBUG, text: 'DEBUG' },
  log: { number: SeverityNumber.INFO, text: 'INFO' },
  info: { number: SeverityNumber.INFO, text: 'INFO' },
  warn: { number: SeverityNumber.WARN, text: 'WARN' },
  error: { number: SeverityNumber.ERROR, text: 'ERROR' },
}

const ALL_LEVELS = Object.keys(LEVELS) as ConsoleLevel[]
/** Bound on a forwarded record's body so one giant log can't bloat the channel. */
const MAX_BODY = 4_096

/** Public handle for the running console forwarder. */
export interface ConsoleForwardHandle {
  /** Restore the original console methods. Idempotent. */
  stop(): void
}

const NOOP: ConsoleForwardHandle = { stop: () => {} }

/** Module-level guard so the forwarder is self-idempotent (one patch per page). */
let active: ConsoleForwardHandle | null = null

/** Resolve the levels to forward from the option (true ⇒ all; array ⇒ as given). */
function resolveLevels(forwardConsole: ObservInitOptions['forwardConsole']): ConsoleLevel[] {
  if (forwardConsole === true) return ALL_LEVELS
  if (Array.isArray(forwardConsole)) return forwardConsole.filter((l) => l in LEVELS)
  return []
}

/** Render one console argument to a string (Error → `name: message`, object → JSON). */
function formatArg(arg: unknown): string {
  if (arg instanceof Error) return `${arg.name}: ${arg.message}`
  if (typeof arg === 'object' && arg !== null) {
    try {
      return JSON.stringify(arg) ?? String(arg)
    } catch {
      return String(arg)
    }
  }
  return String(arg)
}

function formatBody(args: unknown[]): string {
  const body = args.map(formatArg).join(' ')
  return body.length > MAX_BODY ? body.slice(0, MAX_BODY) : body
}

/**
 * Start forwarding the configured `console.*` methods to the logs channel. Returns
 * a handle whose `stop()` restores the originals. Never throws; degrades to a
 * no-op when `forwardConsole` is unset/empty, when `console` is unavailable, or
 * when the logs channel fails to set up.
 */
export function startConsoleForward(options: ObservInitOptions): ConsoleForwardHandle {
  if (active) return active
  const levels = resolveLevels(options.forwardConsole)
  if (levels.length === 0 || typeof console === 'undefined') return NOOP

  let logger: Logger
  try {
    logger = setupOtelLogs(options) // shared, idempotent — same logger as 2.3/2.4
  } catch {
    return NOOP
  }

  // Re-entrancy guard: while we synchronously emit a record, a nested console call
  // (e.g. from deep inside the SDK) must not forward again.
  let emitting = false
  const originals = new Map<ConsoleLevel, (...args: unknown[]) => void>()

  for (const level of levels) {
    const original = console[level] as ((...args: unknown[]) => void) | undefined
    if (typeof original !== 'function') continue
    originals.set(level, original)
    const { number, text } = LEVELS[level]
    console[level] = (...args: unknown[]): void => {
      if (!emitting) {
        emitting = true
        try {
          emitLogRecord(logger, number, text, formatBody(args))
        } catch {
          /* swallow — never let forwarding break a console call */
        } finally {
          emitting = false
        }
      }
      original.apply(console, args)
    }
  }

  let stopped = false
  const handle: ConsoleForwardHandle = {
    stop(): void {
      if (stopped) return
      stopped = true
      active = null
      for (const [level, original] of originals) {
        console[level] = original
      }
      originals.clear()
    },
  }
  active = handle
  return handle
}
